import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';
import { toast } from '@/hooks/use-toast';

export interface BrandMember {
  id: string;
  member_profile_id: string;
  role: 'admin' | 'manager' | 'member';
  status: string;
  permissions: {
    can_view_deals: boolean;
    can_create_deals: boolean;
    can_edit_deals: boolean;
    can_delete_deals: boolean;
    can_view_payments: boolean;
    can_make_payments: boolean;
    can_view_analytics: boolean;
    can_manage_campaigns: boolean;
    can_manage_members: boolean;
    can_view_contacts: boolean;
    can_export_data: boolean;
  };
  profile: {
    display_name: string;
    email: string;
    avatar_url: string;
  };
  joined_at: string;
}

export interface BrandOrganization {
  id: string;
  organization_name: string;
  admin_brand_profile_id: string;
  created_at: string;
  admin_profile?: {
    display_name: string;
    email: string;
    avatar_url: string;
  };
}

export interface OrganizationStats {
  totalDeals: number;
  activeDeals: number;
  completedDeals: number;
  totalDealValue: number;
  totalCampaigns: number;
}

export const useBrandOrganization = () => {
  const { currentUser } = useUser();
  const [organization, setOrganization] = useState<BrandOrganization | null>(null);
  const [members, setMembers] = useState<BrandMember[]>([]);
  const [invites, setInvites] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentMemberPermissions, setCurrentMemberPermissions] = useState<any>(null);
  const [stats, setStats] = useState<OrganizationStats>({
    totalDeals: 0,
    activeDeals: 0,
    completedDeals: 0,
    totalDealValue: 0,
    totalCampaigns: 0,
  });

  const fetchOrganizationData = async () => {
    if (!currentUser?.id) return;

    try {
      setLoading(true);

      // currentUser.id IS the profile_id, not the auth user_id
      const profileId = currentUser.id;

      let { data: org } = await supabase
        .from('brand_organizations')
        .select('*')
        .eq('admin_brand_profile_id', profileId)
        .maybeSingle();

      if (!org) {
        const { data: memberData } = await supabase
          .from('brand_members')
          .select('organization_id, brand_organizations(*), *')
          .eq('member_profile_id', profileId)
          .eq('status', 'active')
          .maybeSingle();

        if (memberData) {
          org = memberData.brand_organizations as any;
          setCurrentMemberPermissions({
            can_view_deals: memberData.can_view_deals,
            can_create_deals: memberData.can_create_deals,
            can_edit_deals: memberData.can_edit_deals,
            can_delete_deals: memberData.can_delete_deals,
            can_view_payments: memberData.can_view_payments,
            can_make_payments: memberData.can_make_payments,
            can_view_analytics: memberData.can_view_analytics,
            can_manage_campaigns: memberData.can_manage_campaigns,
            can_manage_members: memberData.can_manage_members,
            can_view_contacts: memberData.can_view_contacts,
            can_export_data: memberData.can_export_data,
          });
        }
      } else {
        setIsAdmin(true);
      }

      if (!org) {
        setOrganization(null);
        setMembers([]);
        setInvites([]);
        setStats({ totalDeals: 0, activeDeals: 0, completedDeals: 0, totalDealValue: 0, totalCampaigns: 0 });
        return;
      }

      // Fetch admin profile info
      const { data: adminProfile } = await supabase
        .from('profiles')
        .select('display_name, email, avatar_url')
        .eq('id', org.admin_brand_profile_id)
        .single();

      setOrganization({
        ...org,
        admin_profile: adminProfile || undefined,
      });

      const { data: membersData } = await supabase
        .from('brand_members')
        .select(`
          *,
          profiles:member_profile_id (
            display_name,
            email,
            avatar_url
          )
        `)
        .eq('organization_id', org.id)
        .order('joined_at', { ascending: false });

      if (membersData) {
        setMembers(membersData.map((m: any) => ({
          ...m,
          permissions: {
            can_view_deals: m.can_view_deals,
            can_create_deals: m.can_create_deals,
            can_edit_deals: m.can_edit_deals,
            can_delete_deals: m.can_delete_deals,
            can_view_payments: m.can_view_payments,
            can_make_payments: m.can_make_payments,
            can_view_analytics: m.can_view_analytics,
            can_manage_campaigns: m.can_manage_campaigns,
            can_manage_members: m.can_manage_members,
            can_view_contacts: m.can_view_contacts,
            can_export_data: m.can_export_data,
          },
          profile: m.profiles
        })));
      }

      const { data: invitesData } = await supabase
        .from('brand_invite_codes')
        .select('*')
        .eq('organization_id', org.id)
        .order('created_at', { ascending: false });

      if (invitesData) {
        setInvites(invitesData);
      }

      // Fetch organization stats (deals and campaigns for admin profile)
      const { data: dealsData } = await supabase
        .from('deals')
        .select('id, status, deal_value')
        .eq('brand_profile_id', org.admin_brand_profile_id);

      const { data: campaignsData } = await supabase
        .from('campaigns')
        .select('id')
        .eq('brand_profile_id', org.admin_brand_profile_id);

      if (dealsData) {
        setStats({
          totalDeals: dealsData.length,
          activeDeals: dealsData.filter(d => d.status === 'active').length,
          completedDeals: dealsData.filter(d => d.status === 'completed').length,
          totalDealValue: dealsData.reduce((sum, d) => sum + (d.deal_value || 0), 0),
          totalCampaigns: campaignsData?.length || 0,
        });
      }

    } catch (error) {
      console.error('Error fetching organization data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load organization data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizationData();
  }, [currentUser?.id]);

  const generateInvite = async (role: string, permissions: any) => {
    try {
      const { data, error } = await supabase.functions.invoke('generate-brand-invite', {
        body: { role, permissions },
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Invite code generated successfully',
      });

      await fetchOrganizationData();
      return data;
    } catch (error: any) {
      console.error('Error generating invite:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate invite',
        variant: 'destructive',
      });
      return null;
    }
  };

  const linkToOrganization = async (code: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('link-brand-account', {
        body: { code },
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Successfully joined ${data.organization_name}`,
      });

      await fetchOrganizationData();
      return true;
    } catch (error: any) {
      console.error('Error linking account:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to link account',
        variant: 'destructive',
      });
      return false;
    }
  };

  const updateMemberPermissions = async (memberId: string, permissions: any) => {
    try {
      const { error } = await supabase
        .from('brand_members')
        .update(permissions)
        .eq('id', memberId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Member permissions updated',
      });

      await fetchOrganizationData();
      return true;
    } catch (error: any) {
      console.error('Error updating permissions:', error);
      toast({
        title: 'Error',
        description: 'Failed to update permissions',
        variant: 'destructive',
      });
      return false;
    }
  };

  const removeMember = async (memberId: string) => {
    try {
      const { error } = await supabase
        .from('brand_members')
        .update({ status: 'inactive' })
        .eq('id', memberId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Member removed from organization',
      });

      await fetchOrganizationData();
      return true;
    } catch (error: any) {
      console.error('Error removing member:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove member',
        variant: 'destructive',
      });
      return false;
    }
  };

  return {
    organization,
    members,
    invites,
    loading,
    isAdmin,
    currentMemberPermissions,
    stats,
    generateInvite,
    linkToOrganization,
    updateMemberPermissions,
    removeMember,
    refetch: fetchOrganizationData,
  };
};